import FrontendRoutesBase from './FrontendRoutesBase';
import PATH_PARAM, { PAGE_TYPE, PATH_PARAM_DASHBOARD, PATH_PARAM_TOKEN } from '../PATH_PARAM';
import IFrontendRoutes from './IFrontendRoutes';
import { MODEL_PERMISSION } from 'configs/permissions/modelPermissions';

export default class EnterpriseSettingsRoutes extends FrontendRoutesBase implements IFrontendRoutes {
  public root = this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root);
  public serviceProvider: EnterpriseServiceCompaniesRoutes;
  public orderCustomField: OrderCustomFieldRoutes;
  public notificationSettings: NotificationSettingsRoutes;
  public releaseNotes: ReleaseNotesRoutes;

  constructor() {
    super();
    this.serviceProvider = new EnterpriseServiceCompaniesRoutes(this);
    this.orderCustomField = new OrderCustomFieldRoutes(this);
    this.notificationSettings = new NotificationSettingsRoutes(this);
    this.releaseNotes = new ReleaseNotesRoutes(this);
  }

  getInstanceBc: IFrontendRoutes.BreadcrumbsGetter<typeof PATH_PARAM_TOKEN.enterprise> = (_p, permissions, translate) => {
    return permissions.can(MODEL_PERMISSION.CAN_READ_ENTERPRISE_LIST)
      ? [{ name: translate('menus.main.enterprises'), href: this.getListPath() }]
      : [];
  };

  getInstanceChildBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName'> = (p, pm, t, n) => {
    const breadcrumbs = this.getInfoBc(p, pm, t);
    breadcrumbs.push({ name: n.enterpriseName, href: this.getInfoPath(p) });
    return breadcrumbs;
  };

  getListBc = this.getEmptyBreadcrumbs;
  getListPath = () => this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, PAGE_TYPE.list);

  getInfoBc = this.getInstanceBc;
  getInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, params.enterpriseId, PAGE_TYPE.info);

  getAddBc = this.getInstanceBc;
  getAddPath = () => this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, PAGE_TYPE.new, PAGE_TYPE.add);

  getEditBc = this.getInstanceChildBc;
  getEditPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, params.enterpriseId, PAGE_TYPE.edit);

  getOrderSettingsInfoBc = this.getInstanceBc;
  getOrderSettingsInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderSettings,
      PAGE_TYPE.info
    );

  getOrderSettingsEditBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName'> = (
    p,
    pm,
    t,
    n
  ) => {
    const breadcrumbs = this.getInstanceChildBc(p, pm, t, n);
    this.setLastBcLink(breadcrumbs, this.getOrderSettingsInfoPath(p));
    return breadcrumbs;
  };
  getOrderSettingsEditPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderSettings,
      PAGE_TYPE.edit
    );

  getApiSettingsInfoBc = this.getInstanceBc;
  getApiSettingsInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, params.enterpriseId, PATH_PARAM_DASHBOARD.enterprise.apiSettings, PAGE_TYPE.info);

  getUiConsoleInfoBc = this.getInstanceBc;
  getUiConsoleInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, params.enterpriseId, PATH_PARAM_DASHBOARD.enterprise.uiConsole, PAGE_TYPE.info);

  getApiSettingsEditBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName'> = (
    p,
    pm,
    t,
    n
  ) => {
    const breadcrumbs = this.getInstanceChildBc(p, pm, t, n);
    this.setLastBcLink(breadcrumbs, this.getApiSettingsInfoPath(p));
    return breadcrumbs;
  };
  getApiSettingsEditPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, params.enterpriseId, PATH_PARAM_DASHBOARD.enterprise.apiSettings, PAGE_TYPE.edit);
}

class EnterpriseServiceCompaniesRoutes extends FrontendRoutesBase implements IFrontendRoutes {
  constructor(private parent: EnterpriseSettingsRoutes) {
    super();
  }

  getInstanceBc = this.getEmptyBreadcrumbs;

  getInstanceChildBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName'> = (p, pm, t, n) => {
    const breadcrumbs = this.parent.getInstanceChildBc(p, pm, t, n);
    this.setLastBcLink(breadcrumbs, this.getListPath(p));
    return breadcrumbs;
  };

  getListBc = this.parent.getInstanceBc;
  getListPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.serviceCompanies,
      PAGE_TYPE.list
    );

  getEditBc = this.getInstanceChildBc;
  getEditPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.serviceCompanies,
      PAGE_TYPE.edit
    );
}

class OrderCustomFieldRoutes extends FrontendRoutesBase implements IFrontendRoutes {
  constructor(private parent: EnterpriseSettingsRoutes) {
    super();
  }

  getInstanceBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName'> = (p, pm, t, n) => {
    const breadcrumbs = this.parent.getEditBc(p, pm, t, n);
    this.setLastBcLink(breadcrumbs, this.getListPath(p));
    return breadcrumbs;
  };

  getInstanceChildBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName' | 'fieldName'> = (
    p,
    pm,
    t,
    n
  ) => {
    return this.getAddBc(p, pm, t, n).concat([{ name: n.fieldName, href: this.getInfoPath(p) }]);
  };

  getListBc = this.parent.getInstanceBc;
  getListPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      PAGE_TYPE.list
    );

  getInfoBc = this.getInstanceBc;
  getInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      params.customFieldId,
      PAGE_TYPE.info
    );

  getAddBc = this.getInstanceBc;
  getAddPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      PAGE_TYPE.new,
      PAGE_TYPE.add
    );

  getEditBc = this.getInstanceChildBc;
  getEditPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      params.customFieldId,
      PAGE_TYPE.edit
    );
}

class NotificationSettingsRoutes extends FrontendRoutesBase implements IFrontendRoutes {
  constructor(private parent: EnterpriseSettingsRoutes) {
    super();
  }

  getInstanceBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName'> = (p, pm, t, n) => {
    const breadcrumbs = this.parent.getEditBc(p, pm, t, n);
    this.setLastBcLink(breadcrumbs, this.getListPath(p));
    return breadcrumbs;
  };

  getInstanceChildBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName' | 'fieldName'> = (
    p,
    pm,
    t,
    n
  ) => {
    return this.getAddBc(p, pm, t, n).concat([{ name: n.fieldName, href: this.getInfoPath(p) }]);
  };

  getListBc = this.parent.getInstanceBc;
  getListPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      PAGE_TYPE.list
    );

  getInfoBc = this.getInstanceBc;
  getInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      params.customFieldId,
      PAGE_TYPE.info
    );

  getAddBc = this.getInstanceBc;
  getAddPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      PAGE_TYPE.new,
      PAGE_TYPE.add
    );

  getEditBc = this.getInstanceChildBc;
  getEditPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.enterprise.orderCustomFields,
      params.customFieldId,
      PAGE_TYPE.edit
    );

  getNotificationsInfoBc = this.getInstanceBc;
  getNotificationsInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) =>
    this.getDBPath(PATH_PARAM_DASHBOARD.enterprise.root, params.enterpriseId, PATH_PARAM_DASHBOARD.user.notifications, PAGE_TYPE.info);

  getNotificationsEditBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName' | 'userName'> = (
    p,
    pm,
    t,
    n
  ) => {
    return this.getAddBc(p, pm, t, n).concat([{ name: n.userName, href: this.getNotificationsInfoPath(p) }]);
  };

  getNotificationsEditPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = (params) => {
    return this.getDBPath(
      PATH_PARAM_DASHBOARD.enterprise.root,
      params.enterpriseId,
      PATH_PARAM_DASHBOARD.user.notifications,
      PAGE_TYPE.edit
    );
  };
}

class ReleaseNotesRoutes extends FrontendRoutesBase implements IFrontendRoutes {
  constructor(private parent: EnterpriseSettingsRoutes) {
    super();
  }

  getInstanceBc: IFrontendRoutes.BreadcrumbsGetterWithNames<typeof PATH_PARAM_TOKEN.enterprise, 'enterpriseName'> = (p, pm, t, n) => {
    const breadcrumbs = this.parent.getInstanceChildBc(p, pm, t, n);
    this.setLastBcLink(breadcrumbs, this.getListPath(p));
    return breadcrumbs;
  };

  getInstanceChildBc: IFrontendRoutes.BreadcrumbsGetterWithNames<
    typeof PATH_PARAM_TOKEN.enterprise,
    'enterpriseName' | 'technicianTeamName'
  > = (p, pm, t, n) => {
    const breadcrumbs = this.getInstanceBc(p, pm, t, n);
    breadcrumbs.push({ name: n.technicianTeamName, href: this.getInfoPath(p) });
    return breadcrumbs;
  };

  getListBc = this.parent.getInstanceBc;
  getListPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.enterprise> = () =>
    this.getDBPath(PATH_PARAM.dashboard.enterprise.root, PATH_PARAM.dashboard.enterprise.releaseNotes, PAGE_TYPE.list);

  getInfoBc = this.getInstanceBc;
  getInfoPath: IFrontendRoutes.PathGetter<typeof PATH_PARAM_TOKEN.superadmin> = (params) =>
    this.getDBPath(
      PATH_PARAM.dashboard.enterprise.root,
      PATH_PARAM.dashboard.enterprise.releaseNotes,
      params.releaseNoteId,
      PAGE_TYPE.info
    );

  getAddBc = this.getInstanceBc;

  getEditBc = this.getInstanceBc;
}
